import {
  TechnicianAppointmentsItem,
  TechnicianAppointmentsResult,
  TimeEntry,
  UpsertBreakDto,
} from "@myapp/contract";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { appointmentKeys } from "../../../appointment/api/appointment.keys";
import { timeEntryApi } from "../service";

function isSameDay(isoA: string, isoB: string): boolean {
  const a = new Date(isoA);
  const b = new Date(isoB);
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function useUpsertBreak() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dto: UpsertBreakDto) => {
      const { data } = await timeEntryApi.upsertBreak(dto);
      return data as TimeEntry;
    },

    onMutate: async (dto) => {
      const workerQueryKey = [...appointmentKeys.all, "worker", String(dto.assignedTechnicianId)];
      await queryClient.cancelQueries({ queryKey: appointmentKeys.lists() });
      await queryClient.cancelQueries({ queryKey: workerQueryKey });

      const previousQueries = [
        ...queryClient.getQueriesData<TechnicianAppointmentsResult>({
          queryKey: appointmentKeys.lists(),
        }),
        ...queryClient.getQueriesData<TechnicianAppointmentsItem>({
          queryKey: workerQueryKey,
        }),
      ];

      const techId = String(dto.assignedTechnicianId);
      const endTime = new Date(
        new Date(dto.startTime).getTime() + 30 * 60_000,
      ).toISOString();

      function patchBreakOverrides(overrides: TechnicianAppointmentsItem["breakOverrides"]) {
        const existingIdx = overrides.findIndex((b) => isSameDay(b.startTime, dto.startTime));
        if (existingIdx >= 0) {
          const updated = [...overrides];
          updated[existingIdx] = { ...updated[existingIdx], startTime: dto.startTime, endTime };
          return updated;
        }
        return [
          ...overrides,
          {
            id: -1,
            assignedTechnicianId: dto.assignedTechnicianId,
            entryType: "BREAK" as const,
            startTime: dto.startTime,
            endTime,
            createdAt: new Date().toISOString(),
          },
        ];
      }

      // Patch disposition/list view (TechnicianAppointmentsResult — technicians array)
      queryClient.setQueriesData<TechnicianAppointmentsResult>(
        { queryKey: appointmentKeys.lists() },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            technicians: old.technicians.map((tech) =>
              tech.technicianId !== techId
                ? tech
                : { ...tech, breakOverrides: patchBreakOverrides(tech.breakOverrides) },
            ),
          };
        },
      );

      // Patch planning overlay / worker calendar view (TechnicianAppointmentsItem — single technician)
      queryClient.setQueriesData<TechnicianAppointmentsItem>(
        { queryKey: workerQueryKey },
        (old) => {
          if (!old) return old;
          return { ...old, breakOverrides: patchBreakOverrides(old.breakOverrides) };
        },
      );

      return { previousQueries };
    },

    onError: (_err, _vars, context) => {
      if (context?.previousQueries) {
        for (const [queryKey, data] of context.previousQueries) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.all });
    },
  });
}
